import axios from 'axios';
import { aadConfig, getAccessToken } from '../aad';
import type { PhaseTypeKey, ProjectType } from '../dto/projectShare';
import type {
  BudgetRange,
  BuildingCategory,
  ConstructionCategory,
  ConstructionType,
  DeliveryMethod,
  DisciplineSkill,
  EstimateType,
  PhaseType,
  ProcurementMethod,
} from '../dto/projectSupport';
import apiProxy from './apiProxy';

export const getConstructionCategories = apiProxy(
  'getConstructionCategories',
  async (l?: string): Promise<ConstructionCategory[]> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.get(`${process.env.PROJECT_API}/support/constructioncategories${l ? `?l=${l}` : ''}`);
    return data;
  },
);

export const getDisciplineSkills = apiProxy('getDisciplineSkills', async (l?: string): Promise<DisciplineSkill[]> => {
  await getAccessToken(aadConfig().projectScopes);
  const { data } = await axios.get(`${process.env.PROJECT_API}/support/disciplineskills${l ? `?l=${l}` : ''}`);
  return data;
});

export const getConstructionTypes = apiProxy(
  'getConstructionTypes',
  async (l?: string): Promise<ConstructionType[]> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.get(`${process.env.PROJECT_API}/support/constructiontypes${l ? `?l=${l}` : ''}`);
    return Object.keys(data).map((key) => ({ id: `${key[0].toUpperCase()}${key.substring(1)}`, type: data[key] }));
  },
);

export const getPhaseTypes = apiProxy('getPhaseTypes', async (l?: string): Promise<PhaseType[]> => {
  await getAccessToken(aadConfig().projectScopes);
  const { data } = await axios.get(`${process.env.PROJECT_API}/support/phasetypes${l ? `?l=${l}` : ''}`);
  return Object.keys(data).map((key) => ({
    id: `${key[0].toUpperCase()}${key.substring(1)}` as PhaseTypeKey,
    type: data[key],
  }));
});

export const getProcurementMethods = apiProxy(
  'getProcurementMethods',
  async (l?: string): Promise<ProcurementMethod[]> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.get(`${process.env.PROJECT_API}/support/procurementmethods${l ? `?l=${l}` : ''}`);
    return Object.keys(data).map((key) => ({ id: `${key[0].toUpperCase()}${key.substring(1)}`, method: data[key] }));
  },
);

export const getDeliveryMethods = apiProxy('getDeliveryMethods', async (l?: string): Promise<DeliveryMethod[]> => {
  await getAccessToken(aadConfig().projectScopes);
  const { data } = await axios.get(`${process.env.PROJECT_API}/support/deliverymethods${l ? `?l=${l}` : ''}`);
  return Object.keys(data).map((key) => ({ id: `${key[0].toUpperCase()}${key.substring(1)}`, method: data[key] }));
});

export const getBudgetRanges = apiProxy('getBudgetRanges', async (l?: string): Promise<BudgetRange[]> => {
  await getAccessToken(aadConfig().projectScopes);
  const { data } = await axios.get(`${process.env.PROJECT_API}/support/budgetranges${l ? `?l=${l}` : ''}`);
  return Object.keys(data).map((key) => ({ id: `${key[0].toUpperCase()}${key.substring(1)}`, range: data[key] }));
});

export const getEstimateTypes = apiProxy('getEstimateTypes', async (l?: string): Promise<EstimateType[]> => {
  await getAccessToken(aadConfig().projectScopes);
  const { data } = await axios.get(`${process.env.PROJECT_API}/support/estimatetypes${l ? `?l=${l}` : ''}`);
  return Object.keys(data).map((key) => ({ id: `${key[0].toUpperCase()}${key.substring(1)}`, type: data[key] }));
});

export const getBuildingCategories = apiProxy(
  'getBuildingCategories',
  async (l?: string): Promise<BuildingCategory[]> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.get<BuildingCategory[]>(
      `${process.env.PROJECT_API}/support/buildingcategories${l ? `?l=${l}` : ''}`,
    );
    return data;
  },
);

export const updateProjectType = apiProxy(
  'updateProjectType',
  async (projectType: ProjectType, projectId?: string): Promise<ProjectType> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.put(
      `${process.env.PROJECT_API}/project-types/${projectType.id}${projectId ? `?projectId=${projectId}` : ''}`,
      projectType,
    );
    return data;
  },
);
